import React from 'react';
import type { Icon } from '@phosphor-icons/react';
import { tonFarben, type Ton } from './ton';

/**
 * KPI-Kachel in Panels (README §4.4: Wert 20–22 px/800, Radius 20).
 * zeile: Icon-Kachel links, Wert + Label rechts (Mahnwesen, Einheiten-Kennzahlen).
 * stapel: Label oben (Icon rechts), großer Wert, Untertitel (Finanzen, Mietkonto).
 * kachel: Glas, Icon-Box 42×42 / Radius 14, Wert 20/800 über dem Label (Mieter-Panels, Handoff Mieter §4).
 */
export interface KpiTileProps {
  label: string;
  wert: React.ReactNode;
  sub?: React.ReactNode;
  icon?: Icon;
  /** Farbe des Werts und der Icon-Kachel. */
  ton?: Ton;
  layout?: 'zeile' | 'stapel' | 'kachel';
  /** Wert in Tonfarbe färben (z. B. rote Rückstände). */
  wertFarbig?: boolean;
  onClick?: () => void;
}

export function KpiTile({ label, wert, sub, icon: IconKomp, ton = 'neutral', layout = 'stapel', wertFarbig = false, onClick }: KpiTileProps) {
  const { bg, fg } = tonFarben(ton);
  const wertFarbe = wertFarbig ? fg : 'var(--ink)';
  const klickbar = onClick !== undefined;
  const huelle: React.CSSProperties = {
    borderRadius: 20, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)', cursor: klickbar ? 'pointer' : undefined, textAlign: 'left', border: 'none', color: 'inherit',
  };
  const Element = klickbar ? 'button' : 'div';

  if (layout === 'kachel') {
    return (
      <Element type={klickbar ? 'button' : undefined} onClick={onClick} className="immo-glas flex items-center min-w-0"
        style={{ borderRadius: 20, padding: '14px 16px', gap: 12, cursor: klickbar ? 'pointer' : undefined, textAlign: 'left', border: 'none', color: 'inherit' }}>
        {IconKomp && (
          <span className="flex items-center justify-center shrink-0" style={{ width: 42, height: 42, borderRadius: 14, background: bg }}>
            <IconKomp weight="fill" size={20} color={fg} />
          </span>
        )}
        <span className="flex flex-col min-w-0">
          {/* lange Textwerte (z. B. „SEPA-Lastschrift“, Namen) kürzen statt über die Kachel hinauszulaufen */}
          <span className="immo-zahl truncate" title={typeof wert === 'string' ? wert : undefined} style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.1, color: wertFarbe }}>{wert}</span>
          <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{label}</span>
          {sub && <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{sub}</span>}
        </span>
      </Element>
    );
  }

  if (layout === 'zeile') {
    return (
      <Element type={klickbar ? 'button' : undefined} onClick={onClick} className="flex items-center min-w-0" style={{ ...huelle, gap: 14, padding: 16 }}>
        {IconKomp && (
          <span className="flex items-center justify-center shrink-0" style={{ width: 52, height: 52, borderRadius: 16, background: bg }}>
            <IconKomp weight="duotone" size={24} color={fg} />
          </span>
        )}
        <span className="flex flex-col min-w-0" style={{ gap: 2 }}>
          <span className="immo-zahl" style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: wertFarbe }}>{wert}</span>
          <span style={{ fontSize: 13, color: 'var(--ink2)', lineHeight: 1.3 }}>{label}</span>
          {sub && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{sub}</span>}
        </span>
      </Element>
    );
  }

  return (
    <Element type={klickbar ? 'button' : undefined} onClick={onClick} className="flex flex-col min-w-0" style={{ ...huelle, gap: 10, padding: 16 }}>
      <span className="flex items-center justify-between" style={{ gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{label}</span>
        {IconKomp && (
          <span className="flex items-center justify-center shrink-0" style={{ width: 30, height: 30, borderRadius: 10, background: bg }}>
            <IconKomp weight="bold" size={15} color={fg} />
          </span>
        )}
      </span>
      <span className="immo-zahl" style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: wertFarbe }}>{wert}</span>
      {sub && <span style={{ fontSize: 12, color: 'var(--ink2)' }}>{sub}</span>}
    </Element>
  );
}

/** KPI-Reihe: repeat(auto-fit, minmax(170px, 1fr)), gap 10 (README §4.6). */
export function KpiReihe({ children, minBreite = 170 }: { children: React.ReactNode; minBreite?: number }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${minBreite}px, 1fr))`, gap: 10 }}>
      {children}
    </div>
  );
}
